import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Plus, Minus } from "lucide-react";
import { Reveal } from "./Reveal";
import { IMAGES, CAPABILITIES, MARKETS, CATEGORIES } from "./data";

export const HomeHero = () => (
  <section data-testid="home-hero" className="relative min-h-screen flex flex-col justify-end">
    <div className="absolute inset-0">
      <img src={IMAGES.hero} alt="International port and coastline at dusk" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F16]/90 via-[#0A1F16]/45 to-[#0A1F16]/25" />
    </div>

    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full pb-16 pt-48 md:pb-24">
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="text-xs md:text-sm uppercase tracking-[0.3em] font-semibold text-[#BFA26F] mb-6"
        data-testid="hero-overline"
      >
        International Sourcing &amp; Market Development
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.25 }}
        className="font-display text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.05] text-[#FBF9F6] max-w-4xl"
        data-testid="hero-heading"
      >
        Global Sourcing.
        <br />
        <span className="italic text-[#BFA26F]">Regional Market Access.</span>
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="mt-8 text-base md:text-lg font-light text-[#FBF9F6]/85 max-w-2xl leading-relaxed"
        data-testid="hero-subtext"
      >
        Connecting international producers with high-growth consumer markets across the Middle
        East, India and Southeast Asia.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.55 }}
        className="mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-4"
      >
        <Link
          to="/markets"
          data-testid="hero-markets-button"
          className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#BFA26F] text-[#0A1F16] text-sm font-bold tracking-widest uppercase hover:bg-[#FBF9F6] transition-colors duration-300 w-full sm:w-auto"
        >
          Explore Our Markets
          <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform duration-300" />
        </Link>
        <Link
          to="/partner"
          data-testid="hero-supplier-button"
          className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-[#FBF9F6]/40 text-[#FBF9F6] text-sm font-semibold tracking-widest uppercase hover:border-[#BFA26F] hover:text-[#BFA26F] transition-[border-color,color] duration-300 w-full sm:w-auto"
        >
          Become a Supplier
        </Link>
      </motion.div>
    </div>

    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, delay: 0.8 }}
      className="relative border-t border-[#FBF9F6]/15"
      data-testid="hero-region-strip"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-[#FBF9F6]/70 font-medium">
          Middle East <span className="text-[#BFA26F] mx-2">•</span> India
          <span className="text-[#BFA26F] mx-2">•</span> Southeast Asia
        </p>
        <p className="hidden md:block text-xs uppercase tracking-[0.2em] text-[#FBF9F6]/50">
          Sourcing | Procurement | Market Development | Private Label
        </p>
      </div>
    </motion.div>
  </section>
);

export const Capabilities = () => (
  <section data-testid="capabilities-section" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#BFA26F] mb-4" data-testid="overline-rsc-imports">
              RSC Imports
            </p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-[#111413]">
              International Procurement Built Around Market Opportunity.
            </h2>
            <div className="mt-6 h-px w-24 bg-[#BFA26F]" />
            <div className="mt-10 overflow-hidden border border-[#0D2B1E]/10 group">
              <img src={IMAGES.commerce} alt="International commerce and trade" className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7 lg:pt-20">
          <Reveal delay={0.15}>
            <div className="space-y-5 text-base md:text-lg font-light text-[#4A5D54] leading-relaxed" data-testid="capabilities-intro">
              <p>
                RSC Imports is an international sourcing, procurement and market-development company
                connecting global producers with commercial opportunities across the Middle East,
                India and Southeast Asia.
              </p>
              <p>
                Our approach extends beyond conventional trading. We identify products with strong
                international potential, assess their commercial and regulatory viability, develop
                suitable sourcing relationships and determine the most appropriate route to market
                across our target territories.
              </p>
              <p>
                Our focus is on building commercially sustainable relationships between producers,
                importers, distributors and market channels.
              </p>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#0D2B1E]/10" data-testid="capabilities-grid">
        {CAPABILITIES.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.1} className="border-b border-r border-[#0D2B1E]/10">
            <div className="p-8 md:p-10 h-full bg-white/60 hover:bg-white transition-colors duration-300">
              <p className="font-display text-3xl font-medium text-[#BFA26F]/70 mb-6">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-[#111413] mb-4">{c.title}</h3>
              <p className="text-sm font-light text-[#4A5D54] leading-relaxed">{c.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const MarketsTeaser = () => (
  <section data-testid="markets-teaser-section" className="py-24 md:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#BFA26F] mb-4" data-testid="overline-our-markets">
          Our Markets
        </p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-[#111413] max-w-3xl">
          Three Strategic Regions. Multiple Market Opportunities.
        </h2>
        <div className="mt-6 h-px w-24 bg-[#BFA26F]" />
        <p className="mt-8 text-base md:text-lg font-light text-[#4A5D54] max-w-2xl leading-relaxed" data-testid="markets-teaser-intro">
          RSC Imports operates with a multi-market approach to international procurement. Our
          commercial focus across the Middle East, India and Southeast Asia allows suitable
          products to be developed across some of the world's most dynamic consumer markets.
        </p>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-testid="markets-teaser-grid">
        {MARKETS.map((m, i) => (
          <Reveal key={m.name} delay={i * 0.12}>
            <Link to="/markets" className="block group h-full border border-[#0D2B1E]/10 bg-[#FBF9F6] hover:border-[#BFA26F] transition-colors duration-300" data-testid={`market-card-${m.region.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="relative h-52 md:h-60 overflow-hidden">
                <img src={m.image} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F16]/50 to-transparent" />
                <p className="absolute bottom-4 left-6 text-xs uppercase tracking-[0.25em] text-[#FBF9F6] font-semibold">
                  {m.num} — {m.region}
                </p>
              </div>
              <div className="p-8 flex flex-col min-h-[280px]">
                <h3 className="font-display text-2xl font-semibold text-[#111413] mb-4">{m.name}</h3>
                <p className="text-sm font-light text-[#4A5D54] leading-relaxed mb-8">{m.text}</p>
                <p className="mt-auto pt-5 border-t border-[#0D2B1E]/10 text-xs uppercase tracking-[0.2em] text-[#BFA26F] font-semibold">
                  {m.footer}
                </p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const CategoriesGrid = ({ expandable = false, showHeader = true }) => (
  <section data-testid="categories-section" className={showHeader ? "py-24 md:py-32" : ""}>
    <div className={showHeader ? "max-w-7xl mx-auto px-6 lg:px-10" : ""}>
      {showHeader && (
        <Reveal>
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#BFA26F] mb-4" data-testid="overline-global-procurement">
            Global Procurement
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-[#111413] max-w-3xl">
            Products With International Potential.
          </h2>
          <div className="mt-6 h-px w-24 bg-[#BFA26F]" />
          <p className="mt-8 mb-16 text-base md:text-lg font-light text-[#4A5D54] max-w-2xl leading-relaxed" data-testid="categories-intro">
            RSC Imports continuously reviews manufacturers, producers and brands across
            international markets. Our sourcing strategy is driven by product quality, supply
            reliability, commercial scalability, destination-market relevance and long-term growth
            potential.
          </p>
        </Reveal>
      )}
      <CategoryCards expandable={expandable} />
      {showHeader && (
        <Reveal delay={0.2}>
          <Link
            to="/categories"
            data-testid="categories-view-all-link"
            className="group mt-12 inline-flex items-center gap-3 text-sm font-semibold tracking-[0.15em] uppercase text-[#0D2B1E] hover:text-[#BFA26F] transition-colors duration-200"
          >
            View All Categories
            <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </Reveal>
      )}
    </div>
  </section>
);

const CategoryCards = ({ expandable }) => {
  const Card = ({ c, i }) => {
    const body = (
      <>
        <p className="font-display text-3xl font-medium text-[#BFA26F]/70 mb-6">{c.num}</p>
        <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-[#111413] mb-4">{c.title}</h3>
        <p className="text-sm font-light text-[#4A5D54] leading-relaxed">{c.text}</p>
      </>
    );
    return body;
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#0D2B1E]/10" data-testid="categories-grid">
      {CATEGORIES.map((c, i) => (
        <Reveal key={c.title} delay={i * 0.06} className="border-b border-r border-[#0D2B1E]/10">
          <div className="h-full bg-white/60 hover:bg-white transition-colors duration-300 group" data-testid={`category-card-${c.num}`}>
            <div className="h-44 md:h-48 overflow-hidden">
              <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-8">
              <Card c={c} i={i} />
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
};

export const CategoriesAccordion = () => {
  return (
    <div className="border-t border-[#0D2B1E]/10" data-testid="categories-accordion">
      {CATEGORIES.map((c, i) => (
        <AccordionItem key={c.title} c={c} defaultOpen={i === 0} />
      ))}
    </div>
  );
};

const AccordionItem = ({ c, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border-b border-[#0D2B1E]/10">
      <button
        type="button"
        data-testid={`category-toggle-${c.num}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-6 md:gap-10 py-8 md:py-10 text-left group"
      >
        <span className="font-display text-3xl md:text-4xl font-medium text-[#BFA26F]/70 shrink-0 w-14">{c.num}</span>
        <img src={c.image} alt={c.title} className="hidden sm:block w-16 h-16 md:w-20 md:h-20 object-cover shrink-0 border border-[#0D2B1E]/10" />
        <span className="flex-1">
          <span className="block text-sm md:text-base font-bold tracking-[0.15em] uppercase text-[#111413] group-hover:text-[#0D2B1E] transition-colors duration-200">
            {c.title}
          </span>
          <span className="hidden md:block mt-2 text-sm font-light text-[#4A5D54] max-w-2xl">{c.text}</span>
        </span>
        <span className="shrink-0 w-10 h-10 border border-[#0D2B1E]/15 group-hover:border-[#BFA26F] flex items-center justify-center text-[#0D2B1E] transition-colors duration-200">
          {open ? <Minus size={16} strokeWidth={1.5} /> : <Plus size={16} strokeWidth={1.5} />}
        </span>
      </button>
      {open && (
        <div className="pb-10 pl-0 sm:pl-36 md:pl-[172px] pr-4 max-w-4xl" data-testid={`category-detail-${c.num}`}>
          <img src={c.image} alt={c.title} className="sm:hidden w-full h-40 object-cover mb-4" />
          <p className="md:hidden mb-4 text-sm font-light text-[#4A5D54] leading-relaxed">{c.text}</p>
          <p className="text-sm md:text-base font-light text-[#4A5D54] leading-relaxed">{c.detail}</p>
        </div>
      )}
    </div>
  );
};
